import * as Sentry from "@sentry/react";

import {
  useCompletedFormState,
  useFormState,
} from "Store/priceCalculation/priceCalculationHooks";
import { Form, FormikProps } from "formik";
import { useDispatch, useSelector } from "react-redux";
import {
  CARAVAN_DETAILS_STEP,
  CARAVAN_INTRO_STEP,
  CARAVAN_PRICE_STEP,
  Movables,
} from "../";

import { Paragraph } from "App/global-style";
import { CheckPriceProps } from "Components/Features/CheckPrice";
import { BackendProductInfo } from "Components/Features/CheckPrice/Products/productConstants";
import { ProductPriceTile } from "Components/Features/CheckPrice/Tiles/ProductPriceTile";
import { SystemErrorAlert } from "Components/Form/Alert/SystemErrorAlert";
import { ErrorsSummedUp } from "Components/Form/ErrorsSummedUp";
import { NavigationButtons } from "Components/NavigationButtons";
import { useAddProductToCartHandler } from "Store/addProductToCart/hooks";
import { INSURANCE_NAME_VALUE } from "Store/interfaces/products";
import { CaravanCoverage } from "Store/interfaces/products/coverage";
import { updatePriceCalculationFormState } from "Store/priceCalculation/priceCalculationSlice";
import { ReduxState } from "Store/rootReducer";
import { TextLanguage } from "Store/texts/interfaces";
import { mapValidationErrors } from "Utils/formUtils";
import { isNull } from "Utils/functions";
import React from "react";
import { CaravanProductDetailsFormValues } from "../ProductDetails/interfaces";
import { CaravanProductIntroFormValues } from "../ProductIntro/interfaces";
import { CaravanTexts } from "../interface";
import { mapFormToPriceReq } from "../utils";
import { FormElements } from "./FormElements";
import { CaravanProductPriceFormValues } from "./interfaces";
import { validationSchema } from "./validation";

interface Props {
  readonly sendRelatedPriceRequest: Required<CheckPriceProps>["sendRelatedPriceRequest"];
  readonly texts: CaravanTexts;
  readonly lang: TextLanguage;
}

export const CaravanProductPrice = (props: Props): React.ReactElement => {
  const { texts, lang, sendRelatedPriceRequest } = props;
  const dispatch = useDispatch();
  const { error } = useSelector(
    (state: ReduxState) => state.priceCalculation.priceCalculationResult,
  );
  const {
    addProductToCart: submitState,
    priceCalculation: { priceCalculationResult },
  } = useSelector((state: ReduxState) => state);
  const addProductToCart = useAddProductToCartHandler(CARAVAN_PRICE_STEP.id);

  const introFormValues = useCompletedFormState<CaravanProductIntroFormValues>(
    CARAVAN_INTRO_STEP.id,
  );
  const detailsFormValues =
    useCompletedFormState<CaravanProductDetailsFormValues>(
      CARAVAN_DETAILS_STEP.id,
    );
  const initialFormState = useFormState<CaravanProductPriceFormValues>(
    CARAVAN_PRICE_STEP.id,
    { movables: Movables.NONE },
  );

  const updatePrices = (payload: CaravanProductPriceFormValues): void => {
    dispatch(
      updatePriceCalculationFormState({
        stepId: CARAVAN_PRICE_STEP.id,
        formState: payload,
      }),
    );

    sendRelatedPriceRequest(
      BackendProductInfo.CARAVAN.priceReqUrl,
      mapFormToPriceReq(introFormValues, detailsFormValues, payload),
      true,
    );
  };

  const submitHandler = (formValues: CaravanProductPriceFormValues): void => {
    const productToAdd = {
      coverage: formValues.coverage,
      productType: INSURANCE_NAME_VALUE.CARAVAN,
      filterValue:
        formValues.coverage !== CaravanCoverage.CARAVAN_FIRE_AND_THEFT
          ? formValues.deductible
          : undefined,
    };

    addProductToCart(formValues, [productToAdd]);
  };

  return (
    <Sentry.ErrorBoundary
      beforeCapture={(scope) => {
        scope.setTag(
          "location",
          `CheckPrice - ProductPrice - ${INSURANCE_NAME_VALUE.CARAVAN}`,
        );
      }}
    >
      <ProductPriceTile
        header={texts.tile.productPrice.heading[lang]}
        initialValues={initialFormState}
        validationSchema={validationSchema()}
        onSubmit={submitHandler}
      >
        {(formProps: FormikProps<CaravanProductPriceFormValues>) => (
          <Form>
            <Paragraph>{texts.tile.productPrice.ingress[lang]}</Paragraph>

            <FormElements
              formProps={formProps}
              updatePrices={updatePrices}
              texts={texts.formText.checkPrice}
              lang={lang}
            />

            {(!isNull(submitState.error) || !isNull(error)) && (
              <SystemErrorAlert alertId="priceCalculation" />
            )}

            <ErrorsSummedUp
              errors={mapValidationErrors(
                formProps.errors,
                texts.details,
                lang,
              )}
              isVisible
            />

            <NavigationButtons
              isUpsell
              isLoading={submitState.isLoading}
              disableNextButton={priceCalculationResult.isLoading}
            />
          </Form>
        )}
      </ProductPriceTile>
    </Sentry.ErrorBoundary>
  );
};
